import React, { useState, useEffect } from 'react';
import { Typography, Box, Stack, Chip, Grid, Button } from '@mui/material';
import LiveEditor from '../components/Playground/LiveEditor';
import AutobiaModal from '../components/Feedback/AutobiaModal';
import { AutobiaAlert, AutobiaToast } from '../components/Feedback/AutobiaAlert';
import AutobiaLoader from '../components/Loading/AutobiaLoader';
import AutobiaProgressBar from '../components/Feedback/AutobiaProgressBar';
import AutobiaSkeleton, { AutobiaTextSkeleton, AutobiaCardSkeleton, AutobiaTableSkeleton, AutobiaListSkeleton } from '../components/Feedback/AutobiaSkeleton';

const Feedback = () => {
  // Modal states
  const [modalStates, setModalStates] = useState({
    basic: false,
    noClose: false,
    large: false,
  });

  // Toast state
  const [toastState, setToastState] = useState({
    success: false,
    error: false,
    warning: false,
    info: false,
  });

  // Progress state
  const [progress, setProgress] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(45);

  // Simulate progress animation
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => prev >= 100 ? 0 : prev + 10);
    }, 800);

    return () => {
      clearInterval(timer);
    };
  }, []);

  const handleModalOpen = (type) => {
    setModalStates(prev => ({ ...prev, [type]: true }));
  };

  const handleModalClose = (type) => {
    setModalStates(prev => ({ ...prev, [type]: false }));
  };

  const handleToastOpen = (type) => {
    setToastState(prev => ({ ...prev, [type]: true }));
  };

  const handleToastClose = (type) => {
    setToastState(prev => ({ ...prev, [type]: false }));
  };

  // Component Examples
  const AlertsExample = () => (
    <Stack spacing={2}>
      <AutobiaAlert severity="success" title="Success">
        Your order has been placed successfully and will be processed within 24 hours.
      </AutobiaAlert>
      <AutobiaAlert severity="error" title="Error">
        There was an error processing your payment. Please check your card details and try again.
      </AutobiaAlert>
      <AutobiaAlert severity="warning" title="Warning">
        Your session will expire in 5 minutes. Please save your work to avoid losing changes.
      </AutobiaAlert>
      <AutobiaAlert severity="info" title="Information">
        System maintenance is scheduled for tonight from 2:00 AM to 4:00 AM.
      </AutobiaAlert>
    </Stack>
  );

  const LoadersExample = () => (
    <Grid container spacing={4} alignItems="center" justifyContent="center">
      <Grid item>
        <Stack alignItems="center" spacing={2}>
          <AutobiaLoader size="small" />
          <Typography variant="body2">Small</Typography>
        </Stack>
      </Grid>
      <Grid item>
        <Stack alignItems="center" spacing={2}>
          <AutobiaLoader size="medium" />
          <Typography variant="body2">Medium</Typography>
        </Stack>
      </Grid>
      <Grid item>
        <Stack alignItems="center" spacing={2}>
          <AutobiaLoader size="large" />
          <Typography variant="body2">Large</Typography>
        </Stack>
      </Grid>
      <Grid item>
        <Stack alignItems="center" spacing={2}>
          <AutobiaLoader color="#4A60F4" />
          <Typography variant="body2">Custom Color</Typography>
        </Stack>
      </Grid>
    </Grid>
  );

  // Progress Bar Examples
  const BasicProgressExample = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <AutobiaProgressBar
          value={75}
          label="Task Progress"
          helperText="3 of 4 tasks completed"
        />
      </Grid>
      <Grid item xs={12}>
        <AutobiaProgressBar
          value={uploadProgress}
          label="File Upload"
          color="secondary"
        />
      </Grid>
      <Grid item xs={12}>
        <AutobiaProgressBar
          value={30}
          label="Storage Used"
          helperText="Using 3GB of 10GB available"
          showPercentage={false}
        />
      </Grid>
    </Grid>
  );

  const ProgressVariantsExample = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <AutobiaProgressBar
          value={progress}
          label="Animated Progress"
          color="primary"
        />
      </Grid>
      <Grid item xs={12}>
        <AutobiaProgressBar
          variant="indeterminate"
          label="Loading..."
          color="secondary"
          showPercentage={false}
        />
      </Grid>
    </Grid>
  );

  const ProgressSizesExample = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <AutobiaProgressBar
          value={60}
          label="Small Progress Bar"
          size="small"
        />
      </Grid>
      <Grid item xs={12}>
        <AutobiaProgressBar
          value={75}
          label="Medium Progress Bar (Default)"
          size="medium"
        />
      </Grid>
      <Grid item xs={12}>
        <AutobiaProgressBar
          value={90}
          label="Large Progress Bar"
          size="large"
        />
      </Grid>
    </Grid>
  );

  // Skeleton Examples
  const SkeletonExample = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>Text Skeleton</Typography>
        <AutobiaTextSkeleton lines={4} lastLineWidth="70%" />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>Basic Skeleton</Typography>
        <Stack spacing={2}>
          <AutobiaSkeleton variant="rectangular" width="100%" height={40} />
          <AutobiaSkeleton variant="circular" width={60} height={60} />
          <AutobiaSkeleton variant="rounded" width="80%" height={20} />
        </Stack>
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>Card Skeleton</Typography>
        <AutobiaCardSkeleton 
          hasAvatar={true}
          textLines={3}
          hasActions={true}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="h6" sx={{ mb: 2 }}>List Skeleton</Typography>
        <AutobiaListSkeleton 
          items={4}
          hasAvatar={true}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 2 }}>Table Skeleton</Typography>
        <AutobiaTableSkeleton 
          rows={5}
          columns={4}
        />
      </Grid>
    </Grid>
  );

  const ModalsExample = () => (
    <Stack direction="row" spacing={2} flexWrap="wrap">
      <Button 
        variant="contained" 
        onClick={() => handleModalOpen('basic')}
      >
        Open Modal
      </Button>
      <Button 
        variant="outlined" 
        onClick={() => handleModalOpen('noClose')}
      >
        No Close Button
      </Button>
      <Button 
        variant="outlined" 
        color="secondary"
        onClick={() => handleModalOpen('large')}
      >
        Large Modal
      </Button>

      {/* Modals */}
      <AutobiaModal
        open={modalStates.basic}
        onClose={() => handleModalClose('basic')}
        title="Confirmation"
        maxWidth="sm"
      >
        <Typography variant="body1" sx={{ mb: 3 }}>
          Are you sure you want to delete this item? This action cannot be undone.
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => handleModalClose('basic')}>
            Cancel
          </Button>
          <Button variant="contained" color="error">
            Delete
          </Button>
        </Stack>
      </AutobiaModal>

      <AutobiaModal
        open={modalStates.noClose}
        onClose={() => handleModalClose('noClose')}
        title="Processing Payment"
        hideCloseButton={true}
        closeOnBackdropClick={false}
        maxWidth="sm"
      >
        <Stack alignItems="center" spacing={3} sx={{ py: 4 }}>
          <AutobiaLoader />
          <Typography variant="body1" textAlign="center">
            Please wait while we process your payment. Do not close this window.
          </Typography>
          <Button 
            variant="outlined" 
            onClick={() => handleModalClose('noClose')}
          >
            Cancel Payment
          </Button>
        </Stack>
      </AutobiaModal>

      <AutobiaModal
        open={modalStates.large}
        onClose={() => handleModalClose('large')}
        title="Order Details"
        maxWidth="lg"
      >
        <Typography variant="h6" sx={{ mb: 2 }}>Order Summary</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 1 }}>
              Customer Information
            </Typography>
            <Typography variant="body1">John Doe</Typography>
            <Typography variant="body2">john.doe@example.com</Typography>
            <Typography variant="body2">+966 50 123 4567</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 1 }}>
              Shipping Address
            </Typography>
            <Typography variant="body1">123 King Fahd Road</Typography>
            <Typography variant="body2">Riyadh, Saudi Arabia</Typography>
            <Typography variant="body2">Postal Code: 12345</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 1 }}>
              Items Ordered
            </Typography>
            {/* Add table or list of items here */}
            <Box sx={{ p: 2, backgroundColor: 'gray.g50', borderRadius: 1 }}>
              <Typography variant="body2">
                Premium Laptop x1 - 3,250.00 SAR
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </AutobiaModal>
    </Stack>
  );

  const ToastsExample = () => (
    <Stack direction="row" spacing={2} flexWrap="wrap">
      <Button 
        variant="contained" 
        color="success"
        onClick={() => handleToastOpen('success')}
      >
        Success Toast
      </Button>
      <Button 
        variant="contained" 
        color="error"
        onClick={() => handleToastOpen('error')}
      >
        Error Toast
      </Button>
      <Button 
        variant="contained" 
        color="warning"
        onClick={() => handleToastOpen('warning')}
      >
        Warning Toast
      </Button>
      <Button 
        variant="contained" 
        onClick={() => handleToastOpen('info')}
      >
        Info Toast
      </Button>

      {/* Toasts */}
      <AutobiaToast
        open={toastState.success}
        onClose={() => handleToastClose('success')}
        message="Order placed successfully!"
        severity="success"
      />
      <AutobiaToast
        open={toastState.error}
        onClose={() => handleToastClose('error')}
        message="Payment failed. Please try again."
        severity="error"
      />
      <AutobiaToast
        open={toastState.warning}
        onClose={() => handleToastClose('warning')}
        message="Session expiring in 5 minutes."
        severity="warning"
      />
      <AutobiaToast
        open={toastState.info}
        onClose={() => handleToastClose('info')}
        message="New features available in settings."
        severity="info"
      />
    </Stack>
  );

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 6 }}>
        <Typography variant="h2" fontWeight={700} color="gray.g700">
          Feedback Components
        </Typography>
        <Typography variant="body1" color="gray.g600" sx={{ maxWidth: 600 }}>
          Components for providing user feedback including alerts, modals, loading states, 
          and notifications with consistent Autobia styling.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Chip label="User Feedback" color="primary" variant="outlined" />
          <Chip label="Modal Dialogs" variant="outlined" />
          <Chip label="Loading States" variant="outlined" />
        </Stack>
      </Stack>

      <Stack spacing={6}>
        {/* Alerts */}
        <LiveEditor
          title="Alert Messages"
          description="Contextual alert messages for success, error, warning, and informational feedback."
          component={AlertsExample}
          code={`import { AutobiaAlert } from './components/Feedback/AutobiaAlert';

const Example = () => (
  <Stack spacing={2}>
    <AutobiaAlert severity="success" title="Success">
      Your order has been placed successfully and will be processed within 24 hours.
    </AutobiaAlert>
    
    <AutobiaAlert severity="error" title="Error">
      There was an error processing your payment. Please check your card details and try again.
    </AutobiaAlert>
    
    <AutobiaAlert severity="warning" title="Warning">
      Your session will expire in 5 minutes. Please save your work to avoid losing changes.
    </AutobiaAlert>
    
    <AutobiaAlert severity="info" title="Information">
      System maintenance is scheduled for tonight from 2:00 AM to 4:00 AM.
    </AutobiaAlert>
  </Stack>
);

// Props:
// severity: 'success' | 'error' | 'warning' | 'info'
// title: string - Alert title
// onClose: function - Close handler (optional)`}
        />

        {/* Loading Spinners */}
        <LiveEditor
          title="Loading Indicators"
          description="Animated loading spinners in different sizes and colors using Autobia branding."
          component={LoadersExample}
          code={`import AutobiaLoader from './components/Loading/AutobiaLoader';

const Example = () => (
  <Grid container spacing={4} alignItems="center">
    <Grid item>
      <Stack alignItems="center" spacing={2}>
        <AutobiaLoader size="small" />
        <Typography variant="body2">Small</Typography>
      </Stack>
    </Grid>
    
    <Grid item>
      <Stack alignItems="center" spacing={2}>
        <AutobiaLoader size="medium" />
        <Typography variant="body2">Medium</Typography>
      </Stack>
    </Grid>
    
    <Grid item>
      <Stack alignItems="center" spacing={2}>
        <AutobiaLoader size="large" />
        <Typography variant="body2">Large</Typography>
      </Stack>
    </Grid>
    
    <Grid item>
      <Stack alignItems="center" spacing={2}>
        <AutobiaLoader color="#4A60F4" />
        <Typography variant="body2">Custom Color</Typography>
      </Stack>
    </Grid>
  </Grid>
);

// Props:
// size: 'small' | 'medium' | 'large'
// color: string - Custom hex color (defaults to primary)`}
        />

        {/* Progress Bars */}
        <LiveEditor
          title="Progress Bars"
          description="Linear progress bars to show task completion, file uploads, and other progress indicators."
          component={BasicProgressExample}
          code={`import AutobiaProgressBar from './components/Feedback/AutobiaProgressBar';

const [uploadProgress, setUploadProgress] = useState(45);

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <AutobiaProgressBar
        value={75}
        label="Task Progress"
        helperText="3 of 4 tasks completed"
      />
    </Grid>
    <Grid item xs={12}>
      <AutobiaProgressBar
        value={uploadProgress}
        label="File Upload"
        color="secondary"
      />
    </Grid>
    <Grid item xs={12}>
      <AutobiaProgressBar
        value={30}
        label="Storage Used"
        helperText="Using 3GB of 10GB available"
        showPercentage={false}
      />
    </Grid>
  </Grid>
);

// Props:
// value: number (0-100) - Progress value
// label: string - Progress label
// showPercentage: boolean - Show percentage text
// color: 'primary' | 'secondary' - Color variant
// helperText: string - Additional description text
// variant: 'determinate' | 'indeterminate' - Progress type`}
        />

        {/* Progress Variants */}
        <LiveEditor
          title="Progress Variants"
          description="Different progress bar types including animated progress and indeterminate loading states."
          component={ProgressVariantsExample}
          code={`import AutobiaProgressBar from './components/Feedback/AutobiaProgressBar';

const [progress, setProgress] = useState(0);

// Simulate progress animation
useEffect(() => {
  const timer = setInterval(() => {
    setProgress(prev => prev >= 100 ? 0 : prev + 10);
  }, 800);
  return () => clearInterval(timer);
}, []);

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <AutobiaProgressBar
        value={progress}
        label="Animated Progress"
        color="primary"
      />
    </Grid>
    <Grid item xs={12}>
      <AutobiaProgressBar
        variant="indeterminate"
        label="Loading..."
        color="secondary"
        showPercentage={false}
      />
    </Grid>
  </Grid>
);

// Props:
// variant: 'determinate' | 'indeterminate'
// - determinate: Shows specific progress value
// - indeterminate: Shows continuous loading animation`}
        />

        {/* Progress Sizes */}
        <LiveEditor
          title="Progress Bar Sizes"
          description="Progress bars in different sizes for various UI contexts and hierarchy levels."
          component={ProgressSizesExample}
          code={`import AutobiaProgressBar from './components/Feedback/AutobiaProgressBar';

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <AutobiaProgressBar
        value={60}
        label="Small Progress Bar"
        size="small"
      />
    </Grid>
    <Grid item xs={12}>
      <AutobiaProgressBar
        value={75}
        label="Medium Progress Bar (Default)"
        size="medium"
      />
    </Grid>
    <Grid item xs={12}>
      <AutobiaProgressBar
        value={90}
        label="Large Progress Bar"
        size="large"
      />
    </Grid>
  </Grid>
);

// Props:
// size: 'small' | 'medium' | 'large' - Progress bar thickness
// - small: 4px height
// - medium: 8px height (default)
// - large: 12px height`}
        />

        {/* Skeleton Loaders */}
        <LiveEditor
          title="Skeleton Loading States"
          description="Loading placeholders that maintain layout structure while content loads, improving perceived performance."
          component={SkeletonExample}
          code={`import AutobiaSkeleton, { 
  AutobiaTextSkeleton, 
  AutobiaCardSkeleton, 
  AutobiaTableSkeleton, 
  AutobiaListSkeleton 
} from './components/Feedback/AutobiaSkeleton';

const Example = () => (
  <Grid container spacing={3}>
    {/* Text Skeleton */}
    <Grid item xs={12} md={6}>
      <AutobiaTextSkeleton 
        lines={4} 
        lastLineWidth="70%" 
      />
    </Grid>
    
    {/* Basic Skeletons */}
    <Grid item xs={12} md={6}>
      <Stack spacing={2}>
        <AutobiaSkeleton 
          variant="rectangular" 
          width="100%" 
          height={40} 
        />
        <AutobiaSkeleton 
          variant="circular" 
          width={60} 
          height={60} 
        />
        <AutobiaSkeleton 
          variant="rounded" 
          width="80%" 
          height={20} 
        />
      </Stack>
    </Grid>

    {/* Card Skeleton */}
    <Grid item xs={12} md={6}>
      <AutobiaCardSkeleton 
        hasAvatar={true}
        textLines={3}
        hasActions={true}
      />
    </Grid>

    {/* List Skeleton */}
    <Grid item xs={12} md={6}>
      <AutobiaListSkeleton 
        items={4}
        hasAvatar={true}
      />
    </Grid>

    {/* Table Skeleton */}
    <Grid item xs={12}>
      <AutobiaTableSkeleton 
        rows={5}
        columns={4}
      />
    </Grid>
  </Grid>
);

// AutobiaSkeleton Props:
// variant: 'rectangular' | 'circular' | 'rounded' - Shape
// width: string | number - Width (100%, 200px, etc.)
// height: string | number - Height
// animation: 'pulse' | 'wave' | false - Animation type

// AutobiaTextSkeleton Props:
// lines: number - Number of text lines
// spacing: number - Spacing between lines
// lastLineWidth: string - Width of last line (60%, 200px)

// AutobiaCardSkeleton Props:
// hasAvatar: boolean - Show avatar skeleton
// hasImage: boolean - Show image skeleton
// imageHeight: number - Image skeleton height
// textLines: number - Number of text lines
// hasActions: boolean - Show action buttons skeleton

// AutobiaTableSkeleton Props:
// rows: number - Number of table rows
// columns: number - Number of table columns

// AutobiaListSkeleton Props:
// items: number - Number of list items
// hasAvatar: boolean - Show avatar skeletons
// hasIcon: boolean - Show icon skeletons`}
        />

        {/* Modals */}
        <LiveEditor
          title="Modal Dialogs"
          description="Flexible modal components with different sizes and configuration options."
          component={ModalsExample}
          code={`import AutobiaModal from './components/Feedback/AutobiaModal';

const [modalOpen, setModalOpen] = useState(false);

const Example = () => (
  <>
    <Button onClick={() => setModalOpen(true)}>
      Open Modal
    </Button>
    
    <AutobiaModal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      title="Confirmation"
      maxWidth="sm"
    >
      <Typography variant="body1" sx={{ mb: 3 }}>
        Are you sure you want to delete this item? This action cannot be undone.
      </Typography>
      
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={() => setModalOpen(false)}>
          Cancel
        </Button>
        <Button variant="contained" color="error">
          Delete
        </Button>
      </Stack>
    </AutobiaModal>
  </>
);

// Props:
// maxWidth: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
// hideCloseButton: boolean
// closeOnBackdropClick: boolean
// showDivider: boolean`}
        />

        {/* Toast Notifications */}
        <LiveEditor
          title="Toast Notifications"
          description="Temporary notification messages that appear at the top of the screen."
          component={ToastsExample}
          code={`import { AutobiaToast } from './components/Feedback/AutobiaAlert';

const [toastOpen, setToastOpen] = useState(false);

const Example = () => (
  <>
    <Button onClick={() => setToastOpen(true)}>
      Show Toast
    </Button>
    
    <AutobiaToast
      open={toastOpen}
      onClose={() => setToastOpen(false)}
      message="Order placed successfully!"
      severity="success"
      autoHideDuration={6000}
      position={{ vertical: 'top', horizontal: 'center' }}
    />
  </>
);

// Props:
// severity: 'success' | 'error' | 'warning' | 'info'
// message: string - Toast message
// autoHideDuration: number - Auto-hide time in ms
// position: object - { vertical, horizontal }`}
        />
      </Stack>

      {/* Usage Guidelines */}
      <Box sx={{ mt: 8, p: 4, backgroundColor: 'gray.g25', borderRadius: 2 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Feedback Guidelines
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Alert Usage
            </Typography>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 2 }}>
              Use alerts for persistent messages that require user acknowledgment. 
              Choose appropriate severity levels based on the message importance.
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Modal Best Practices
            </Typography>
            <Typography variant="body2" color="gray.g600">
              Use modals sparingly for critical actions that require focus. 
              Always provide clear action buttons and escape options.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Loading States
            </Typography>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 2 }}>
              Show loading indicators for operations taking longer than 1 second. 
              Use skeleton loaders for content areas and spinners for actions.
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Toast Notifications
            </Typography>
            <Typography variant="body2" color="gray.g600">
              Use toasts for brief confirmations and non-critical updates. 
              Keep messages concise and set appropriate auto-hide durations.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Feedback;